import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  Alert,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Paper
} from '@mui/material';

const ReceiptSettings = () => {
  const [settings, setSettings] = useState({
    businessName: '',
    businessAddress: '',
    businessPhone: '',
    businessEmail: '',
    receiptHeader: '',
    receiptFooter: '',
    showTaxBreakdown: true,
    showItemCodes: false,
    showCashierName: true,
    thankYouMessage: 'Thank you for your business!'
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch('/api/settings/receipt');
      if (res.ok) {
        const data = await res.json();
        if (data) {
          setSettings(prev => ({ ...prev, ...data }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch receipt settings:', err);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const res = await fetch('/api/settings/receipt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        setMessage({ text: 'Receipt settings saved successfully!', type: 'success' });
      } else {
        const error = await res.json();
        setMessage({ text: error.error || 'Failed to save settings', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Network error. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Box>
      <Stack spacing={3}>
        {/* Business Information */}
        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" fontWeight={600} color="primary" mb={2}>
            Business Information
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Business Name"
              fullWidth
              size="small"
              value={settings.businessName}
              onChange={(e) => handleChange('businessName', e.target.value)}
              placeholder="Your Store Name"
            />
            <TextField
              label="Business Address"
              fullWidth
              size="small"
              multiline
              rows={2}
              value={settings.businessAddress}
              onChange={(e) => handleChange('businessAddress', e.target.value)}
              placeholder="Street Address, City, State, ZIP"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Phone Number"
                fullWidth
                size="small"
                value={settings.businessPhone}
                onChange={(e) => handleChange('businessPhone', e.target.value)}
                placeholder="+1 (555) 123-4567"
              />
              <TextField
                label="Email Address"
                fullWidth
                size="small"
                type="email"
                value={settings.businessEmail}
                onChange={(e) => handleChange('businessEmail', e.target.value)}
                placeholder="info@business.com"
              />
            </Stack>
          </Stack>
        </Paper>

        {/* Receipt Customization */}
        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" fontWeight={600} color="primary" mb={2}>
            Receipt Customization
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Receipt Header Text"
              fullWidth
              size="small"
              multiline
              rows={2}
              value={settings.receiptHeader}
              onChange={(e) => handleChange('receiptHeader', e.target.value)}
              placeholder="Custom header text (optional)"
              helperText="Appears at the top of receipt after business info"
            />
            <TextField
              label="Receipt Footer Text"
              fullWidth
              size="small"
              multiline
              rows={2}
              value={settings.receiptFooter}
              onChange={(e) => handleChange('receiptFooter', e.target.value)}
              placeholder="Returns policy, warranty info, etc."
              helperText="Appears at the bottom of receipt"
            />
            <TextField
              label="Thank You Message"
              fullWidth
              size="small"
              value={settings.thankYouMessage}
              onChange={(e) => handleChange('thankYouMessage', e.target.value)}
              placeholder="Thank you for your business!"
            />
          </Stack>
        </Paper>

        {/* Display Options */}
        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" fontWeight={600} color="primary" mb={2}>
            Display Options
          </Typography>
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.showTaxBreakdown}
                  onChange={(e) => handleChange('showTaxBreakdown', e.target.checked)}
                />
              }
              label="Show Tax Breakdown"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.showItemCodes}
                  onChange={(e) => handleChange('showItemCodes', e.target.checked)}
                />
              }
              label="Show Item/SKU Codes"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.showCashierName}
                  onChange={(e) => handleChange('showCashierName', e.target.checked)}
                />
              }
              label="Show Cashier Name"
            />
          </Stack>
        </Paper>

        {message.text && (
          <Alert severity={message.type} onClose={() => setMessage({ text: '', type: '' })}>
            {message.text}
          </Alert>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading}
          sx={{ alignSelf: 'flex-start' }}
        >
          {loading ? 'Saving...' : 'Save Receipt Settings'}
        </Button>
      </Stack>
    </Box>
  );
};

export default ReceiptSettings;
